import { DialogContent, type DialogContentPrimitiveProps, DialogFooter, DialogHeader } from '../dialog';
import type { ButtonProps } from '@components/molecules';
import { Button, ButtonSize, ButtonVariant, BackButton } from '@components/molecules';
import { cn, type CompositionProp } from '@utils';

type DialogProfileLayoutProps = React.PropsWithChildren<
	{
		headerContent?: React.ReactNode;
		footerDescription?: CompositionProp;
		primaryButton?: CompositionProp;
		secondaryButton?: CompositionProp;
		primaryButtonProps?: Omit<ButtonProps, 'type' | 'onClick' | 'variant'>;
		secondaryButtonProps?: Omit<ButtonProps, 'type' | 'onClick' | 'variant'>;
		onPrimaryAction?: () => void;
		onSecondaryAction?: () => void;
		onBackAction?: () => void;
		closeIcon?: boolean;
		sectionDivider?: boolean;
		headerClassName?: string;
		contentClassName?: string;
	} & DialogContentPrimitiveProps
>;

export const DialogProfileLayout: React.FC<DialogProfileLayoutProps> = ({
	headerContent,
	children,
	footerDescription,
	primaryButton,
	secondaryButton,
	primaryButtonProps,
	secondaryButtonProps,
	onPrimaryAction,
	onSecondaryAction,
	onBackAction,
	closeIcon = true,
	sectionDivider = true,
	className,
	headerClassName,
	contentClassName,
	...props
}) => {
	const footerDescriptionComponent =
		typeof footerDescription === 'string' ? (
			<p className="text-sm text-secondary">{footerDescription}</p>
		) : (
			(footerDescription as React.ReactNode)
		);

	const primaryButtonComponent =
		typeof primaryButton === 'string' ? (
			<Button {...primaryButtonProps} size={ButtonSize.SM} type="submit" onClick={onPrimaryAction}>
				{primaryButton}
			</Button>
		) : (
			(primaryButton as React.ReactNode)
		);

	const secondaryButtonComponent =
		typeof secondaryButton === 'string' ? (
			<Button
				{...secondaryButtonProps}
				variant={ButtonVariant.SECONDARY}
				size={ButtonSize.SM}
				onClick={onSecondaryAction}
			>
				{secondaryButton}
			</Button>
		) : (
			(secondaryButton as React.ReactNode)
		);

	return (
		<DialogContent
			backButtonComponent={onBackAction ? <BackButton onAction={onBackAction}>Back</BackButton> : undefined}
			closeIcon={closeIcon}
			className={cn('flex max-h-[90vh] w-[520px] flex-col sm:max-w-[95vw]', className)}
			{...props}
		>
			<div
				className={cn(`flex h-full flex-col overflow-y-auto ${sectionDivider ? 'divide-y divide-borderSeparator' : ''} min-h-[42vh]`, contentClassName)}
			>
				<DialogHeader className={cn('shrink-0 justify-items-center pb-2', headerClassName)}>{headerContent}</DialogHeader>
				<div className="max-h-[60vh] shrink overflow-y-auto px-10">{children}</div>
				<DialogFooter className="flex shrink-0 flex-col items-center justify-between gap-2 p-4 md:flex-row md:items-start md:space-x-4">
					<div className="text-center md:text-left">{footerDescription && footerDescriptionComponent}</div>
					<div className="flex w-full flex-col justify-center space-y-2 md:w-auto md:flex-row md:space-x-4 md:space-y-0">
						{onSecondaryAction && secondaryButtonComponent}
						{primaryButtonComponent}
					</div>
				</DialogFooter>
			</div>
		</DialogContent>
	);
};
